import type {
  AckMessageType,
  HandlerFunction,
  HelpCenterBridgeMessage,
  HostBridgeMessage,
} from './types'
import { HelpCenterMessageType, HostMessageType } from './types'

let helpcenterMessagePort: MessagePort | null = null
let helpCenterIframeUrl: string | null = null
let handlers: Partial<Record<HelpCenterMessageType, HandlerFunction>> = {}

export const useBridgeCommunication = () => {
  const startBridgeCommunication = (iframeUrl: string) => {
    helpCenterIframeUrl = iframeUrl
    window.addEventListener('message', _performHandshake)
  }

  const closeBridgeCommunication = () => {
    window.removeEventListener('message', _performHandshake)
    helpcenterMessagePort?.close()
    helpcenterMessagePort = null
    handlers = {}
  }

  const addMessageHandler = (type: HelpCenterMessageType, handler: HandlerFunction) => {
    handlers[type] = handler
  }

  const sendMessage = (message: HostBridgeMessage) => {
    if (!helpcenterMessagePort) {
      return
    }

    helpcenterMessagePort.postMessage({
      id: `${message.type}-${new Date().getTime()}`,
      ...message,
    })
  }

  const _performHandshake = (event: MessageEvent) => {
    if (!helpCenterIframeUrl) {
      return
    }
    const {
      origin,
      data: message,
      ports: [port],
    } = event

    /**
     * Discard messages not coming from the iframe URL.
     */
    if (
      origin !== new URL(helpCenterIframeUrl).origin &&
      !origin.endsWith('helpcenter-webclient-testing.pages.dev')
    ) {
      return
    }

    /**
     * The first message we expect is HELPCENTER_INIT_STARTED, once received:
     * we use the port provided for next communications,
     * we sent a HOST_IS_READY message.
     */
    if (message.type === HelpCenterMessageType.HELPCENTER_INIT_STARTED) {
      helpcenterMessagePort = port
      helpcenterMessagePort.onmessage = _handleMessages

      sendMessage({
        type: HostMessageType.HOST_IS_READY,
        payload: {
          isHelpcenterFullScreen: false,
          supportedMessageTypes: Object.values(HelpCenterMessageType),
        },
      })
    }
  }

  const _handleMessages = (event: MessageEvent<HelpCenterBridgeMessage>): void => {
    const { data: message } = event

    if (!message) {
      return
    }

    if (!Object.values(HelpCenterMessageType).includes(message.type)) {
      return
    }

    _ackMessage(message)

    if (handlers[message.type] && typeof handlers[message.type] === 'function') {
      handlers[message.type]!(message)
    }
  }

  const _ackMessage = (message: HelpCenterBridgeMessage) => {
    if (!helpcenterMessagePort) {
      return
    }
    sendMessage({
      type: `${message.type}_ACK` as AckMessageType,
      id: message.id,
    })
  }

  return { startBridgeCommunication, closeBridgeCommunication, addMessageHandler, sendMessage }
}
